<script setup lang="ts">
import { onMounted } from 'vue';
import { useApp } from './stores/app';
import HomeView from './views/HomeView.vue';
import EditorView from './views/EditorView.vue';
import SettingsView from './views/SettingsView.vue';
import ProfileView from './views/ProfileView.vue';
import CardEditorView from './views/CardEditorView.vue';
import Toasts from './components/Toasts.vue';
import NavDrawer from './components/NavDrawer.vue';
import AppDialog from './components/AppDialog.vue';
import InstallDialog from './components/InstallDialog.vue';
import ModuleWindows from './components/ModuleWindows.vue';
import ConsentDialog from './components/ConsentDialog.vue';
import ProfileLock from './components/ProfileLock.vue';
import WhatsNewDialog from './components/WhatsNewDialog.vue';
import FeedbackDialog from './components/FeedbackDialog.vue';
import NoticeBanner from './components/NoticeBanner.vue';
import ChatPanel from './components/ChatPanel.vue';
import StorageBanner from './components/StorageBanner.vue';
import { initModules, startCatalogInstall } from './modules';
import { installLinkFromHash, loadCatalog } from './catalog';
import { makeThumbnail } from './editor/thumbnails';
import { live, ui } from './editor/bridge';
import { GLASS_LENS_MAP } from './glass-lens';

const app = useApp();
// a closed canvas keeps an up-to-date preview on the home screen
app.setBeforeClose(async (s) => {
  const c = live.session === s ? live.renderer?.camera : null;
  if (c) s.setCamera({ cx: c.cx, cy: c.cy, zoom: c.zoom });
  await s.flush();
  await makeThumbnail(s);
});

/** The showcase's Install button: #install=<id>@<version> → the confirmation, on the modules page. */
async function openInstallLink(): Promise<boolean> {
  const link = installLinkFromHash();
  if (!link) return false;
  history.replaceState(null, '', '#/modules');
  app.go('modules');
  void loadCatalog();
  await startCatalogInstall(link.id, link.version);
  return true;
}
window.addEventListener('hashchange', () => void openInstallLink());

onMounted(async () => {
  // the canvas list does not depend on modules: show it first
  const listed = app.refreshCanvases();
  await initModules(app);
  await listed;
  if (await openInstallLink()) return;
  await app.applyHash();
});
</script>

<template>
  <svg class="glass-lens" width="0" height="0" aria-hidden="true">
    <filter id="vp-glass" x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">
      <feImage preserveAspectRatio="none" result="map" :href="GLASS_LENS_MAP" />
      <feDisplacementMap in="SourceGraphic" in2="map" scale="34" xChannelSelector="R" yChannelSelector="G" />
    </filter>
  </svg>
  <!-- the banner is a bar above everything: the views below it get the rest of the height -->
  <NoticeBanner />
  <div class="app-main">
    <HomeView v-if="app.view === 'home'" />
    <EditorView v-show="app.view === 'editor'" v-if="app.tabs.length" />
    <SettingsView v-if="app.view === 'settings'" />
    <ProfileView v-if="app.view === 'profile'" />
    <CardEditorView v-if="app.view === 'cards'" />
  </div>
  <ChatPanel v-if="ui.chatOpen" v-show="app.view !== 'editor' || !ui.zen" />
  <NavDrawer />
  <Toasts />
  <StorageBanner />
  <AppDialog />
  <InstallDialog />
  <ModuleWindows />
  <FeedbackDialog />
  <WhatsNewDialog />
  <ConsentDialog />
  <ProfileLock />
</template>
